import { FunctionComponent, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeBranch, faXmark, faPlus } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import CriteriaPanel from '../Trigger/CriteriaPanel';
import { type CriterionValue } from '@spike/automation-model';
import { buildCriterionChipLabel } from '../Trigger/criterionLabels';
import { useCriteriaCatalog } from '../../hooks/useCriteriaCatalog';
import styles from './SplitSidebar.module.scss';

export interface SplitConfig {
    conditions: CriterionValue[];
}

interface Props {
    initialConfig?: SplitConfig;
    onSave?: (config: SplitConfig) => void;
    onCancel?: () => void;
    onConfigChange?: (config: SplitConfig) => void;
}

const SplitSidebar: FunctionComponent<Props> = ({ initialConfig, onSave, onCancel, onConfigChange }) => {
    const [conditions, setConditions] = useState<CriterionValue[]>(initialConfig?.conditions ?? []);
    const [showCriteria, setShowCriteria] = useState(false);
    const { byField } = useCriteriaCatalog();

    useEffect(() => {
        onConfigChange?.({ conditions });
    }, [conditions, onConfigChange]);

    const getLabelForCriterion = (c: CriterionValue) => buildCriterionChipLabel(c, byField);

    const handleRemove = (id: string) => setConditions(prev => prev.filter(c => c.id !== id));

    const handleSave = () => onSave?.({ conditions });

    const canSave = conditions.length > 0;

    return (
        <aside className={styles.sidebar}>
            {/* ── CriteriaPanel overlay ── */}
            {showCriteria && (
                <CriteriaPanel
                    title="Add conditions"
                    selected={conditions}
                    onBack={() => setShowCriteria(false)}
                    onConfirm={newConditions => {
                        setConditions(newConditions);
                        setShowCriteria(false);
                    }}
                />
            )}

            <div className={styles.body}>
                {/* Header */}
                <div className={styles.nodeHeader}>
                    <div className={styles.iconCircle}>
                        <FontAwesomeIcon icon={faCodeBranch} />
                    </div>
                    <div className={styles.headerInfo}>
                        <span className={styles.headerTitle}>Conditional Split</span>
                        <span className={styles.headerDesc}>Branch the flow based on conditions.</span>
                    </div>
                    {onCancel && (
                        <button
                            type="button"
                            className={styles.closeBtn}
                            onClick={onCancel}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    )}
                </div>

                {/* YES / NO explanation */}
                <div className={styles.branchInfo}>
                    <div className={styles.branchRow}>
                        <span className={styles.branchBadgeYes}>YES</span>
                        <span className={styles.branchDesc}>All conditions are met</span>
                    </div>
                    <div className={styles.branchRow}>
                        <span className={styles.branchBadgeNo}>NO</span>
                        <span className={styles.branchDesc}>At least one condition fails</span>
                    </div>
                </div>

                {/* Conditions section */}
                <div className={styles.section}>
                    <div className={styles.sectionRow}>
                        <div>
                            <div className={styles.sectionLabel}>Conditions</div>
                            <div className={styles.sectionSubLabel}>
                                {conditions.length === 0
                                    ? 'No conditions added yet'
                                    : `${conditions.length} condition${conditions.length > 1 ? 's' : ''} added`}
                            </div>
                        </div>
                        <button
                            type="button"
                            className={styles.addBtn}
                            onClick={() => setShowCriteria(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                            {conditions.length === 0 ? 'Add' : 'Edit'}
                        </button>
                    </div>

                    {conditions.length > 0 && (
                        <div className={styles.chips}>
                            {conditions.map(c => (
                                <div
                                    key={c.id}
                                    className={styles.chip}
                                >
                                    <span>{getLabelForCriterion(c)}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemove(c.id)}
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.footer}>
                <Button
                    label="Save"
                    variant="green"
                    fullWidth
                    disabled={!canSave}
                    onClick={handleSave}
                />
            </div>
        </aside>
    );
};

export default SplitSidebar;
